import React, { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { KeyRound } from "lucide-react";
import { api, LOGO_URL } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

const formatErr = (d) => {
  if (!d) return "Something went wrong";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return d?.msg || String(d);
};

export default function ForceChangePassword() {
  const { user, refreshMe } = useAuth();
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  if (user && !user.must_change_password) {
    return <Navigate to={user.role === "admin" ? "/admin" : "/account"} replace />;
  }
  if (!user) return <Navigate to="/signin" replace />;

  const submit = async (e) => {
    e.preventDefault();
    if (password !== confirm) { setError("Passwords don't match"); return; }
    if (password.length < 6) { setError("Password must be at least 6 characters"); return; }
    setLoading(true); setError("");
    try {
      await api.post("/auth/force-change-password", { new_password: password });
      await refreshMe();
      toast.success("Password updated. Welcome to MileForge.");
      navigate(user.role === "admin" ? "/admin" : "/account");
    } catch (err) {
      setError(formatErr(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  return (
    <div data-testid="force-change-password-page" className="min-h-[80vh] flex items-center justify-center px-6 py-16">
      <div className="w-full max-w-md border border-white/10 p-8 lg:p-10 bg-[#0E0E0E]">
        <img src={LOGO_URL} alt="MileForge" className="h-14 w-auto object-contain mb-3 -ml-3" />
        <div className="flex items-center gap-2 overline mb-1"><KeyRound size={14} /> Security Step</div>
        <h1 className="font-display text-3xl uppercase tracking-tight">Set A New Password</h1>
        <p className="text-sm text-white/55 font-body mt-2">Your account was created with a temporary password. Set your own to continue.</p>

        <form onSubmit={submit} className="mt-7 space-y-4">
          <div>
            <label className="overline block mb-2">New Password</label>
            <input data-testid="force-change-password" required type="password" minLength={6} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="At least 6 characters" className="w-full bg-[#111] border border-white/15 px-4 py-3 focus:outline-none focus:border-[#D96A1B] font-body" />
          </div>
          <div>
            <label className="overline block mb-2">Confirm Password</label>
            <input data-testid="force-change-confirm" required type="password" minLength={6} value={confirm} onChange={(e) => setConfirm(e.target.value)} className="w-full bg-[#111] border border-white/15 px-4 py-3 focus:outline-none focus:border-[#D96A1B] font-body" />
          </div>
          {error && <div data-testid="force-change-error" className="text-sm text-[#D96A1B] font-body">{error}</div>}
          <button data-testid="force-change-submit" disabled={loading} className="btn-orange w-full justify-center">
            {loading ? "Updating..." : "Set Password & Continue"}
          </button>
        </form>
      </div>
    </div>
  );
}
